// src/components/Toast.js
// Lightweight toast notification system

import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, AlertCircle, Bell, Info } from 'lucide-react';

const ToastContext = createContext(null);

const TOAST_ICONS = {
  success: Check,
  error: AlertCircle,
  info: Info,
  notify: Bell,
};

const TOAST_COLORS = {
  success: 'text-[#A3E635]',
  error: 'text-rose-400',
  info: 'text-zinc-300',
  notify: 'text-[#A3E635]',
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((options) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      type: options.type || 'info',
      title: options.title,
      message: options.message,
      duration: options.duration ?? 3000,
    };

    setToasts((prev) => [...prev, toast]);

    if (toast.duration > 0) {
      setTimeout(() => dismissToast(id), toast.duration);
    }

    return id;
  }, [dismissToast]);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, onDismiss }) => {
  return (
    <div className="fixed bottom-6 right-6 z-[200] flex flex-col gap-2 pointer-events-none">
      <AnimatePresence>
        {toasts.map((toast) => {
          const Icon = TOAST_ICONS[toast.type] || Info;
          const color = TOAST_COLORS[toast.type] || 'text-zinc-300';

          return (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="pointer-events-auto bg-[#181818] border border-white/[0.08] rounded-2xl shadow-2xl px-4 py-3 min-w-[280px] max-w-sm flex items-start gap-3"
              style={{
                boxShadow: '0 10px 40px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255,255,255,0.05)',
              }}
            >
              <div className={`shrink-0 w-8 h-8 rounded-full bg-white/[0.04] border border-white/[0.06] flex items-center justify-center ${color}`}>
                <Icon size={14} />
              </div>
              <div className="flex-1 pt-0.5">
                {toast.title && (
                  <div className="text-[13px] font-bold text-white leading-tight">
                    {toast.title}
                  </div>
                )}
                {toast.message && (
                  <div className={`text-[12px] text-zinc-400 ${toast.title ? 'mt-1' : ''}`}>
                    {toast.message}
                  </div>
                )}
              </div>
              <button
                onClick={() => onDismiss(toast.id)}
                className="shrink-0 text-zinc-600 hover:text-white transition-colors -mr-1 -mt-1 p-1"
              >
                <X size={14} />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

/**
 * Hook to access toast functions
 * Usage: const { showToast } = useToast();
 *        showToast({ type: 'success', title: 'Subscribed', message: '...' });
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export default ToastProvider;